import { useEffect } from "react";
import SEO from "@/components/SEO";
import { AD_PAGES, member } from "@/data/adLanding";
import { CLINIC } from "@/data/content";
import { useLandingVisit } from "@/lib/attribution";
import { trackEvent } from "@/lib/analytics";
import BookingPanel from "@/components/adlanding/BookingPanel";
import {
  Wordmark, HeroImage, PractitionerCompact, OfferLine, ProofStrip, Steps, CredentialsStrip, AdFooter, StickyBar,
} from "@/components/adlanding/Parts";

const AdLanding = ({ page }) => {
  const cfg = AD_PAGES[page];
  const { utm, source, markInteracting } = useLandingVisit(cfg.path, cfg.serviceKey);
  const many = cfg.practitioners.length > 1;
  const defaultFormat = cfg.formats && /regional/i.test(utm.utm_campaign || "") ? "video" : "inPerson";

  useEffect(() => {
    trackEvent("landing_view", { page: cfg.path, source });
  }, [cfg.path, source]);

  return (
    <div className="bg-warm-white min-h-screen" data-testid={`ad-landing-${page}`}>
      <SEO title={cfg.title} description={cfg.description} noindex />

      <div className="lg:grid lg:grid-cols-[55fr_45fr] lg:min-h-screen">
        {/* Left column */}
        <div className="relative">
          <div className="hidden lg:block absolute inset-0 overflow-hidden" aria-hidden="true">
            <HeroImage hero={cfg.hero} className="w-full h-full opacity-30" />
            <div className="absolute inset-0 bg-warm-white/70" />
          </div>
          <HeroImage hero={cfg.hero} className="lg:hidden w-full aspect-[4/5] max-h-[70vh]" />

          <div className="relative px-6 py-8 sm:px-10 lg:px-16 lg:py-12 flex flex-col gap-8 lg:min-h-screen">
            <Wordmark />
            <div>
              <p className="text-[11px] tracking-[0.2em] uppercase text-gold" data-testid="ad-eyebrow">{cfg.eyebrow}</p>
              <h1 className="font-serif font-light text-deep-sea text-4xl sm:text-5xl lg:text-[60px] leading-[1.05] mt-4" data-testid="ad-h1">{cfg.h1}</h1>
              <p className="font-serif italic font-light text-[22px] leading-snug text-soft-grey mt-5 max-w-xl" data-testid="ad-standfirst">{cfg.standfirst}</p>
            </div>

            <div>
              {cfg.practitionerNote && <p className="text-ink/75 text-sm mb-4" data-testid="ad-practitioner-note">{cfg.practitionerNote}</p>}
              <div className={many ? "grid grid-cols-2 sm:grid-cols-4 gap-5" : ""}>
                {cfg.practitioners.map((slug) => (
                  <PractitionerCompact key={slug} slug={slug} compact={many} credentialLine={cfg.credentialLine} />
                ))}
              </div>
            </div>

            <OfferLine items={cfg.offer} />
            <ProofStrip items={cfg.proof} />
          </div>
        </div>

        {/* Right column */}
        <div className="px-6 py-10 sm:px-10 lg:px-12 lg:py-12 lg:flex lg:items-start">
          <div className="w-full">
            <BookingPanel
              page={cfg.path}
              source={source}
              heading={cfg.widgetHeading}
              widgetKey={cfg.widget}
              formats={cfg.formats}
              defaultFormat={defaultFormat}
              fifo={cfg.fifo}
              practitioner={many ? "Any physician" : member(cfg.practitioners[0]).name}
              onInteract={markInteracting}
            />
          </div>
        </div>
      </div>

      {/* Below the fold */}
      <div className="px-6 sm:px-10 lg:px-16 max-w-6xl mx-auto">
        <CredentialsStrip lines={cfg.credentials} />
        <Steps steps={cfg.steps} note={cfg.stepsNote} />
        {cfg.standards && (
          <p className="text-ink/75 text-sm leading-relaxed max-w-3xl -mt-6 mb-14" data-testid="ad-standards">{cfg.standards}</p>
        )}
        <section className="py-12 border-t border-hairline/40" data-testid="ad-discretion">
          <h2 className="font-serif font-light text-deep-sea text-3xl">Discretion</h2>
          <p className="text-ink/80 text-base leading-relaxed mt-4 max-w-3xl">{cfg.discretion}</p>
          <p className="text-soft-grey text-sm mt-3">{CLINIC.address}</p>
        </section>
        <AdFooter />
      </div>
      <div className="h-[72px] lg:hidden" aria-hidden="true" />
      <StickyBar targetId="booking-panel" />
    </div>
  );
};

export default AdLanding;
